import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/auth-context';
import { useLanguage } from '../contexts/language-context';
import html2canvas from 'html2canvas';
import { CONSOLE_MESSAGES, INPUT_LIMITS, UI_LABELS, LIST_MESSAGES } from '../constants';
import { SPINNER_CLASSES, BUTTON_CLASSES, MODAL_CLASSES, cn } from '../styles';

function ShareModal({ isOpen, onClose, item, mediaType }) {
  const [review, setReview] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const shareCardRef = useRef(null);
  const { getWatchedItem } = useAuth();
  const { t } = useLanguage();

  const title = item?.title || item?.name || '';
  const year = new Date(item?.release_date || item?.first_air_date).getFullYear();
  const posterPath = item?.poster_path 
    ? `https://image.tmdb.org/t/p/w500${item.poster_path}`
    : null;

  // Get existing review if user has watched this item
  const watchedItem = getWatchedItem(item?.id, mediaType);

  // Initialize with existing review if available
  useEffect(() => {
    if (isOpen && item) {
      if (watchedItem?.comment) {
        setReview(watchedItem.comment);
      } else {
        setReview('');
      }
      setShowPreview(false);
    }
  }, [isOpen, item, watchedItem]);

  // Close modal when clicking outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isGenerating) {
      onClose();
    }
  };

  // Handle preview toggle
  const handleTogglePreview = () => {
    setShowPreview(!showPreview);
  };

  // Generate and download the share image
  const handleGenerateImage = async () => {
    if (!shareCardRef.current) return;

    setIsGenerating(true);
    try {
      // Show preview temporarily for screenshot
      const wasPreviewShown = showPreview;
      setShowPreview(true);

      // Wait for the DOM to update
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: '#0f1419',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: 600,
        windowWidth: 600,
      });

      // Restore preview state
      if (!wasPreviewShown) {
        setShowPreview(false);
      }

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_review.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });

    } catch (error) {
      console.error(CONSOLE_MESSAGES.ERROR_GENERATING_SHARE_IMAGE, error);
      alert(LIST_MESSAGES.SHARE_IMAGE_ERROR);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!isGenerating) {
      setReview('');
      setShowPreview(false);
      onClose();
    }
  };

  if (!isOpen || !item) return null;

  return (
    <div 
      className={MODAL_CLASSES.OVERLAY}
      onClick={handleBackdropClick}
    >
      <div className={cn(MODAL_CLASSES.CONTAINER_LG, 'max-h-[90vh] overflow-y-auto')}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {UI_LABELS.SHARE_REVIEW}
              </h2>
              <p className="text-gray-400 text-sm">
                {title} ({year})
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isGenerating}
            className={cn(
              MODAL_CLASSES.CLOSE_BUTTON,
              'hover:bg-dark-700 rounded-lg',
              isGenerating && 'cursor-not-allowed opacity-50'
            )}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Review Section */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              {UI_LABELS.YOUR_REVIEW}
            </label>
            <textarea
              value={review}
              onChange={(e) => setReview(e.target.value)}
              maxLength={INPUT_LIMITS.COMMENT_MAX_LENGTH}
              rows={4}
              className="input-primary w-full resize-none"
              placeholder={UI_LABELS.WRITE_REVIEW_PLACEHOLDER}
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-500">
                {watchedItem?.rating && (
                  <span className="text-yellow-400 mr-2">
                    {'★'.repeat(watchedItem.rating)}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500">
                {review.length}/{INPUT_LIMITS.COMMENT_MAX_LENGTH}
              </p>
            </div>
          </div>

          {/* Preview Toggle */}
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={handleTogglePreview}
              className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              {showPreview ? 'Slėpti peržiūrą' : 'Peržiūrėti vaizdą'}
            </button>
          </div>

          {/* Share Card Preview - Hidden by default but exists for screenshot */}
          <div 
            ref={shareCardRef}
            className={cn(
              'transition-all duration-300',
              showPreview ? 'block' : 'hidden'
            )}
          >
            <div className="relative w-[600px] bg-gradient-to-br from-[#0f1419] via-[#1a2332] to-[#0f1419] overflow-hidden">
              {/* Decorative Background Elements */}
              <div className="absolute inset-0">
                {/* Grid Pattern */}
                <div className="absolute inset-0 opacity-[0.03]" style={{
                  backgroundImage: 'linear-gradient(#6366f1 1px, transparent 1px), linear-gradient(90deg, #6366f1 1px, transparent 1px)',
                  backgroundSize: '50px 50px'
                }} />
                {/* Gradient Orbs */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
              </div>

              {/* Content Container */}
              <div className="relative">
                {/* Header */}
                <div className="px-10 pt-10 pb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                        </svg>
                      </div>
                      <div>
                        <h1 className="text-2xl font-bold text-white tracking-tight">FilmuDB</h1>
                        <p className="text-xs text-primary-400 font-medium">filmudb.lt</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Apžvalga</div>
                      <div className="text-sm text-gray-400">{year}</div>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="px-10 pb-8">
                  <div className="bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-sm rounded-2xl border border-primary-500/20 shadow-2xl overflow-hidden">
                    {/* Movie Section */}
                    <div className="p-8">
                      <div className="flex gap-6">
                        {/* Poster */}
                        {posterPath && (
                          <div className="flex-shrink-0">
                            <div className="relative group">
                              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/20 to-purple-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all"></div>
                              <img
                                src={posterPath}
                                alt={title}
                                className="relative w-40 h-60 object-cover rounded-xl shadow-2xl border-2 border-primary-500/30"
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Movie Info */}
                        <div className="flex-1 flex flex-col justify-between min-w-0">
                          <div>
                            <h2 className="text-3xl font-bold text-white leading-tight mb-4 break-words">
                              {title}
                            </h2>
                            
                            {/* Ratings */}
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                              {item.vote_average > 0 && (
                                <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2">
                                  <svg className="w-5 h-5 text-yellow-400 fill-current flex-shrink-0" viewBox="0 0 24 24">
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                  </svg>
                                  <span className="text-lg font-bold text-yellow-400 leading-none">
                                    {item.vote_average.toFixed(1)}
                                  </span>
                                  <span className="text-xs text-gray-400 leading-none">TMDB</span>
                                </div>
                              )}
                            </div>

                            {/* Description - always show */}
                            {item.overview && (
                              <div className="mt-4">
                                <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">
                                  {t('description')}
                                </h4>
                                <p className="text-gray-300 text-sm leading-relaxed">
                                  {item.overview}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* User Review */}
                      {review && (
                        <div className="mt-6 pt-6 border-t border-dark-600">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-purple-500 flex items-center justify-center">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="text-gray-300 text-base leading-relaxed italic">
                                {review}
                              </p>
                              {watchedItem?.rating && (
                                <div className="flex items-center gap-1 mt-3">
                                  {[...Array(watchedItem.rating)].map((_, i) => (
                                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 24 24">
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Info Message */}
          {!review && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-blue-300">
                  {t('shareReviewHint')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between p-6 border-t border-dark-700 bg-dark-900/50">
          <button
            type="button"
            onClick={handleClose}
            disabled={isGenerating}
            className={cn(
              'px-4 py-2 text-gray-300 hover:text-white transition-colors font-medium',
              isGenerating && 'cursor-not-allowed opacity-50'
            )}
          >
            {UI_LABELS.CANCEL}
          </button>
          <button
            type="button"
            onClick={handleGenerateImage}
            disabled={isGenerating}
            className={cn(
              'btn-primary px-6 py-3 flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800',
              BUTTON_CLASSES.DISABLED
            )}
          >
            {isGenerating ? (
              <>
                <div className={SPINNER_CLASSES.MEDIUM_WHITE}></div>
                <span>Generuojama...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>{UI_LABELS.DOWNLOAD_IMAGE}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShareModal;

